import { AmplitudeProvider } from 'Contexts/AmplitudeContext';
import CustomProviders from 'Contexts/CustomProviders';
import { DialogProvider } from 'Contexts/DialogContext';
import { OnboardingTourProvider } from 'Contexts/OnboardingTourContext';
import { SentryUserProvider } from 'Contexts/SentryUserContext';
import { TermsProvider } from 'Contexts/TermsContext';
import { TrackNavigationEventsProvider } from 'Contexts/TrackNavigationEvents';
import { WelcomeSlidesProvider } from 'Contexts/WelcomeSlidesContext';
import {
  CompanyPlanProvider,
  useCompanyPlanContext,
} from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { useFirebase } from 'FeatureModules/Auth/Contexts/FirebaseContext';
import useUserData from 'FeatureModules/Auth/hooks/useUserData';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import { UploadProvider } from 'FeatureModules/Document/Contexts/UploadContext';
import React, { FC, memo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import ClientUIRouter from './ClientUIRoutes';
import PartnerUIRouter from './PartnerUIRoutes';

/** These providers can only be created and used when the user is authenticated */
const AUTH_ROUTER_PROVIDERS = [
  UploadProvider as FC, // Needs to be on top of the dialog provider
  SentryUserProvider,
  AmplitudeProvider,
  TrackNavigationEventsProvider,
  CompanyPlanProvider,
  DialogProvider,
  TermsProvider,
  WelcomeSlidesProvider,
  OnboardingTourProvider,
];

const AuthenticatedRoute: FC = () => {
  const { plan } = useCompanyPlanContext();
  const { pathname, search } = useLocation();
  const { user, company } = useUserData();

  // check if user data in cache is sufficient
  const localStateSufficient = user && company;

  if (!localStateSufficient) {
    return (
      <Navigate
        to="/login"
        state={{
          redirect: `${pathname}${search}`,
        }}
      />
    );
  }

  return <>{plan === ECustomerPlan.FREEMIUM ? <PartnerUIRouter /> : <ClientUIRouter />}</>;
};

// Wrap the auth providers around the AuthenticatedRoute
const AuthenticatedRouter: FC = () => {
  const { authenticated } = useFirebase();
  // It would be better to use no globals here and the useLocation hook from react-router-dom
  // but the location hook takes ways too long to update and introduces a flickering in the drawer
  // eslint-disable-next-line no-restricted-globals
  const { pathname, search } = location;

  if (!authenticated) {
    return (
      <Navigate
        to="/login"
        state={{
          redirect: `${pathname}${search}`,
        }}
      />
    );
  }

  return (
    <CustomProviders providers={AUTH_ROUTER_PROVIDERS}>
      <AuthenticatedRoute />
    </CustomProviders>
  );
};

export default memo(AuthenticatedRouter);
